import { environment } from '../config/environment';
import { useApiClient } from './api';

export interface AdminCheckResponse {
    isAdmin: boolean;
    email: string;
}

export interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    createdAt: string;
    isVerified: boolean;
    status: 'active' | 'inactive' | 'pending';
    role: 'user' | 'admin';
}

export interface UserPlan {
    id: string;
    userId: string;
    name: string;
    year?: string;
    taxYear?: string;
    type: 'taxReport' | 'expertReview' | 'addon';
    package?: string;
    addonType?: string;
    status: 'active' | 'expired' | 'cancelled';
    transactionLimit?: number;
    purchaseDate: string;
    validUntil?: string;
    expiryDate?: string;
    stripePaymentId?: string;
    isAddon?: boolean;
    validated?: boolean;
    isCustomPricing?: boolean;
}

export interface UserLog {
    id: string;
    action: string;
    timestamp: string;
    details: string | null;
}

export interface UserLogsResponse {
    logs: UserLog[];
}

export interface IndividualPlan {
    price: number;
    currency: string;
}

export interface IndividualPlanResponse {
    success: boolean;
    plan: IndividualPlan;
}

export interface SetIndividualPlanRequest {
    price: number;
}

export interface SetIndividualPlanResponse {
    success: boolean;
    message: string;
    price: number;
}

/**
 * Custom hook for admin-related functionality
 */
export const useAdminService = () => {
    const apiClient = useApiClient();

    /**
     * Check if the current user is an admin
     * @returns Promise<AdminCheckResponse>
     */
    const checkIsAdmin = async (): Promise<AdminCheckResponse> => {
        try {
            const response = await apiClient.get<AdminCheckResponse>(
                `${environment.expressApiUrl}/api/admin/check-admin`
            );
            return response.data;
        } catch (error) {
            console.error('Error checking admin status:', error);
            return { isAdmin: false, email: '' };
        }
    };

    /**
     * Get all users from the system
     * @returns Promise<User[]>
     */
    const getUsers = async (): Promise<User[]> => {
        try {
            const response = await apiClient.get<User[]>(`${environment.expressApiUrl}/api/admin/users`);
            return response.data;
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    };

    /**
     * Get user plans
     * @param userId - The user ID
     * @returns Promise<UserPlan[]>
     */
    const getUserPlans = async (userId: string): Promise<UserPlan[]> => {
        try {
            const response = await apiClient.get<{ plans: UserPlan[] }>(
                `${environment.expressApiUrl}/api/admin/users/${userId}/plans`
            );
            return response.data.plans;
        } catch (error) {
            console.error('Error fetching user plans:', error);
            throw error;
        }
    };

    /**
     * Get user activity logs
     * @param userId - The user ID
     * @returns Promise<UserLog[]>
     */
    const getUserLogs = async (userId: string): Promise<UserLog[]> => {
        try {
            const response = await apiClient.get<UserLogsResponse>(
                `${environment.expressApiUrl}/api/admin/users/${userId}/logs`
            );
            return response.data.logs;
        } catch (error) {
            console.error('Error fetching user logs:', error);
            throw error;
        }
    };

    /**
     * Get a custom token to login as a specific user
     * @param userId - The user ID to login as
     * @returns Promise<string> - The custom token
     */
    const loginAsUser = async (userId: string): Promise<string> => {
        try {
            const response = await apiClient.post<{ customToken: string }>(
                `${environment.expressApiUrl}/api/admin/users/${userId}/login-as`
            );
            return response.data.customToken;
        } catch (error) {
            console.error('Error generating login token:', error);
            throw error;
        }
    };

    /**
     * Validate a user's "We Do Everything" addon
     * @param userId - The user ID
     * @returns Promise<void>
     */
    const validateWeDoEverything = async (userId: string): Promise<void> => {
        try {
            await apiClient.post(`${environment.expressApiUrl}/api/admin/users/${userId}/validate-we-do-everything`);
        } catch (error) {
            console.error('Error validating We Do Everything addon:', error);
            throw error;
        }
    };

    /**
     * Get individual plan for a user
     * @param userId - The user ID
     * @returns Promise<IndividualPlan>
     */
    const getIndividualPlan = async (userId: string): Promise<IndividualPlan> => {
        try {
            const response = await apiClient.get<IndividualPlanResponse>(
                `${environment.expressApiUrl}/api/admin/users/${userId}/individual-plan`
            );
            return response.data.plan;
        } catch (error) {
            console.error('Error fetching individual plan:', error);
            throw error;
        }
    };

    /**
     * Set individual plan price for a user
     * @param userId - The user ID
     * @param price - The price in cents
     * @returns Promise<SetIndividualPlanResponse>
     */
    const setIndividualPlan = async (userId: string, price: number): Promise<SetIndividualPlanResponse> => {
        try {
            const response = await apiClient.post<SetIndividualPlanResponse>(
                `${environment.expressApiUrl}/api/admin/users/${userId}/individual-plan`,
                { price }
            );
            return response.data;
        } catch (error) {
            console.error('Error setting individual plan:', error);
            throw error;
        }
    };

    /**
     * Delete individual plan for a user
     * @param userId - The user ID
     * @returns Promise<void>
     */
    const deleteIndividualPlan = async (userId: string): Promise<void> => {
        try {
            await apiClient.delete(`${environment.expressApiUrl}/api/admin/users/${userId}/individual-plan`);
        } catch (error) {
            console.error('Error deleting individual plan:', error);
            throw error;
        }
    };

    return {
        checkIsAdmin,
        getUsers,
        getUserPlans,
        getUserLogs,
        loginAsUser,
        validateWeDoEverything,
        getIndividualPlan,
        setIndividualPlan,
        deleteIndividualPlan,
    };
};
